import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import aiIcon from '../../../../../../assets/icons/ai-icon.svg';
import { buildEntryHistoryItems, getEntryHistoryTitle } from './entryHistoryUtils';
import '../../../../../repositorio/HistoryList.styles.scss';
import './EntryHistoryList.scss';

const PASCALE_BOT_EMAIL = 'bot@smartcfo.cl';

const isPascaleBotEmail = (email) =>
  String(email || '')
    .toLowerCase()
    .trim() === PASCALE_BOT_EMAIL;

const translateAction = (action, t) => {
  const key = `entryHistory.actions.${action}`;
  const translated = t(key, { defaultValue: '' });
  return translated || action?.toLowerCase() || '';
};

const getActionColor = (action) => {
  switch (action) {
    case 'CREATED':
      return '#3c4a5e';
    case 'UPDATED':
      return '#3c4a5e';
    case 'DELETE':
      return '#dc2626';
    case 'REVERSED':
      return '#d97706';
    default:
      return '#176f80';
  }
};

const getActionIcon = (action) => {
  if (action === 'REVERSED') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <polyline points="3 3 3 9 9 9" />
      </svg>
    );
  }
  if (action === 'UPDATED') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    );
  }
  if (action === 'CREATED') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
    );
  }
  if (action === 'DELETE') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
};

const HistoryClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const HistorySectionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M12 8v4l2.5 1.5" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

const EntryHistoryUser = ({ email, t }) => {
  if (isPascaleBotEmail(email)) {
    return (
      <span className="entry-pascale-badge entry-history-list__user-badge" title={t('entryRow.pascaleAssistant')}>
        Pascale
        <img src={aiIcon} alt="" aria-hidden />
      </span>
    );
  }

  return <span className="history-list-user">{email}</span>;
};

const isGenericActionSummary = (summary, t) => {
  if (!summary) return true;
  return [
    t('entryHistory.created'),
    t('entryHistory.updated'),
    t('entryHistory.deleted'),
    t('entryHistory.reversed'),
  ].includes(summary);
};

const EntryHistoryValueDiff = ({ oldValue, newValue, t, className = '' }) => (
  <div className={`entry-history-change__stack ${className}`.trim()}>
    <div className="entry-history-change__value entry-history-change__value--before">
      <span className="entry-history-change__value-tag">{t('entryHistory.before')}</span>
      <span className="entry-history-change__value-text">{oldValue}</span>
    </div>
    <div className="entry-history-change__value entry-history-change__value--after">
      <span className="entry-history-change__value-tag">{t('entryHistory.after')}</span>
      <span className="entry-history-change__value-text">{newValue}</span>
    </div>
  </div>
);

const AMOUNT_PATTERN = /(\$\s?[\d.]+(?:,\d+)?)/g;

const renderHistoryText = (value, variant) =>
  String(value ?? '')
    .split(AMOUNT_PATTERN)
    .filter(Boolean)
    .map((part, index) =>
      part.startsWith('$') ? (
        <span
          key={`${variant}-${index}`}
          className={`entry-history-line-diff__amount entry-history-line-diff__amount--${variant}`}
        >
          {part}
        </span>
      ) : (
        <span key={`${variant}-${index}`}>{part}</span>
      ),
    );

const EntryHistoryFieldDiff = ({ label, oldValue, newValue }) => (
  <div className="entry-history-line-diff__field">
    {label && <span className="entry-history-line-diff__field-label">{label}</span>}
    <span className="entry-history-line-diff__from">{renderHistoryText(oldValue, 'from')}</span>
    <span className="entry-history-line-diff__arrow" aria-hidden>→</span>
    <span className="entry-history-line-diff__to">{renderHistoryText(newValue, 'to')}</span>
  </div>
);

const EntryHistoryChange = ({ change, t }) => {
  if (change.type === 'summary') {
    return (
      <div className="entry-history-change entry-history-change--summary">
        <span className="entry-history-change__summary">{change.summary}</span>
      </div>
    );
  }

  if (change.type === 'lineDiffs') {
    return (
      <div className="entry-history-change entry-history-change--lines">
        <div className="entry-history-change__label">{change.label}</div>
        {change.summary && (
          <div className="entry-history-change__summary entry-history-change__summary--muted">
            {change.summary}
          </div>
        )}
        <div className="entry-history-line-diffs">
          {change.lineDiffs.map((lineDiff, lineIndex) => (
            <div
              key={`line-diff-${lineIndex}`}
              className={`entry-history-line-diff entry-history-line-diff--${lineDiff.kind}`}
            >
              {lineDiff.label && (
                <div className="entry-history-line-diff__title">{lineDiff.label}</div>
              )}
              {lineDiff.kind !== 'modified' && lineDiff.summary && (
                <div className="entry-history-line-diff__summary">{lineDiff.summary}</div>
              )}
              {lineDiff.fields?.map((field, fieldIndex) => (
                <EntryHistoryFieldDiff
                  key={`${field.label || 'change'}-${fieldIndex}`}
                  label={field.label}
                  oldValue={field.oldValue}
                  newValue={field.newValue}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="entry-history-change entry-history-change--stacked">
      <div className="entry-history-change__label">{change.label}</div>
      <EntryHistoryValueDiff oldValue={change.oldValue} newValue={change.newValue} t={t} />
    </div>
  );
};

const EntryHistoryItem = ({ item, t }) => {
  const showSummary =
    Boolean(item.summary) &&
    !item.changes?.length &&
    !isGenericActionSummary(item.summary, t);

  return (
    <div className="history-list-row">
      <div className={`history-list-item entry-history-list__item entry-history-list__item--${String(item.action || '').toLowerCase()}`}>
        <div className="history-list-avatar" style={{ backgroundColor: getActionColor(item.action) }}>
          {getActionIcon(item.action)}
        </div>
        <div className="history-list-body">
          <div className="history-list-header entry-history-list__header">
            <div className="entry-history-list__headline">
              <EntryHistoryUser email={item.userEmail} t={t} />
              <span className="history-list-action-verb">{translateAction(item.action, t)}</span>
              <span className="history-list-timeline-label">{t('entryHistory.timelineLabel')}</span>
            </div>
            <span className="history-list-time entry-history-list__time">
              <HistoryClockIcon />
              {item.date} {t('entryHistory.atTime')} {item.time || '00:00'}
            </span>
          </div>

          {showSummary && (
            <div className="entry-history-list__summary">{item.summary}</div>
          )}

          {item.changes?.length > 0 && (
            <div className="entry-history-changes">
              {item.changes.map((change, changeIndex) => (
                <EntryHistoryChange key={`${change.key}-${changeIndex}`} change={change} t={t} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EntryHistoryList = ({ entry, showHeader = true, embedded = false }) => {
  const { t } = useTranslation('accounting');
  const history = useMemo(() => buildEntryHistoryItems(entry, t), [entry, t]);
  const displayTitle = useMemo(() => getEntryHistoryTitle(entry, t), [entry, t]);

  const items = history.length === 0 ? (
    <div className="history-list-empty entry-history-list__empty">
      <p>{t('entryHistory.empty')}</p>
    </div>
  ) : (
    <div className="history-list-items">
      <div className={`history-list-items-inner ${history.length >= 2 ? 'history-list-items-inner--multiple' : ''}`}>
        {history.map((item, index) => (
          <EntryHistoryItem key={item.id || index} item={item} t={t} />
        ))}
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="entry-history-embedded">
        <header className="entry-history-embedded__header">
          <div className="entry-history-embedded__title-group">
            <span className="entry-history-embedded__icon">
              <HistorySectionIcon />
            </span>
            <h3 className="entry-history-embedded__title">{t('entryHistory.title')}</h3>
          </div>
          {history.length > 0 && (
            <span className="entry-history-embedded__count">
              {t('entryHistory.eventCount', { count: history.length })}
            </span>
          )}
        </header>
        <div className="entry-history-embedded__body">{items}</div>
      </div>
    );
  }

  return (
    <div className="history-list-container entry-history-list">
      {showHeader && (
        <div className="history-list-period-header">
          <div className="history-list-period-header-inner">
            <div className="history-list-period-info-row">
              <div className="history-list-period-left-group">
                <div className="history-list-period-title">{displayTitle}</div>
                <span className="history-list-period-separator">|</span>
                <span className="history-list-period-count">
                  {t('entryHistory.eventCount', { count: history.length })}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {items}
    </div>
  );
};

export default EntryHistoryList;
